/**
 * Extracción de páginas de un PDF/imagen a JPEG base64 + texto embebido,
 * pensada para facturas EMITIDAS (PDFs digitales generados por un ERP).
 * No aplica auto-cropping ni OCR agresivo: los documentos tienen capa de texto.
 */
import { getPdfDocument } from "@/lib/pdfjs";

export interface ExtractedDoc {
  images: string[];
  text: string;
}

const extractText = (items: any[]): string => {
  const parts: string[] = [];
  let lastY: number | null = null;
  for (const item of items) {
    const str = typeof item?.str === "string" ? item.str : "";
    const y = Array.isArray(item?.transform) ? Math.round(item.transform[5]) : null;
    if (lastY !== null && y !== null && Math.abs(y - lastY) > 2) parts.push("\n");
    parts.push(str);
    if (y !== null) lastY = y;
  }
  return parts.join(" ").replace(/[ \t]+/g, " ").replace(/\n\s+/g, "\n").trim();
};

export const isImageFile = (file: File): boolean =>
  file.type.startsWith("image/") || /\.(png|jpe?g)$/i.test(file.name);

export const imageFileToBase64 = (file: File): Promise<string[]> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const base64 = result.split(",")[1];
      resolve(base64 ? [base64] : []);
    };
    reader.onerror = () => reject(new Error("No se pudo leer la imagen"));
    reader.readAsDataURL(file);
  });

/** Renderiza las páginas relevantes (primeras 2 + última) y devuelve JPEG base64. */
export const extractIssuedPdf = async (file: File): Promise<ExtractedDoc> => {
  const arrayBuffer = await file.arrayBuffer();
  const loadingTask = await getPdfDocument({ data: arrayBuffer });
  const pdf = await loadingTask.promise;

  const pages = new Set<number>();
  for (let i = 1; i <= Math.min(pdf.numPages, 2); i++) pages.add(i);
  pages.add(pdf.numPages);
  const sorted = Array.from(pages).sort((a, b) => a - b);

  const pageTexts: Record<number, string> = {};
  let nativeChars = 0;
  for (const i of sorted) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent({ includeMarkedContent: false } as any);
    const txt = extractText(content.items as any[]);
    pageTexts[i] = txt;
    nativeChars += txt.replace(/\s+/g, "").length;
  }
  const isScanned = nativeChars < 40;

  const images: string[] = [];
  const textParts: string[] = [];
  for (const i of sorted) {
    const page = await pdf.getPage(i);
    const viewport = page.getViewport({ scale: isScanned ? 2.6 : 1.7 });
    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext("2d")!;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: ctx, viewport } as any).promise;
    images.push(canvas.toDataURL("image/jpeg", isScanned ? 0.9 : 0.8).split(",")[1]);
    textParts.push(pageTexts[i] || "");
    canvas.width = 0;
    canvas.height = 0;
  }

  await loadingTask.destroy();
  return { images, text: textParts.join("\n---\n") };
};
